import { Injectable, signal } from '@angular/core';

/**
 * Tracks in-flight HTTP requests (fed by the SlowConnectionInterceptor) and
 * flags a slow connection when the app stays busy loading past the threshold.
 *
 * A "loading run" spans a near-continuous sequence of requests: brief gaps
 * between back-to-back requests are bridged (see `idleGraceMs`) so the elapsed
 * time isn't lost each time one request finishes and the next starts. The slow
 * countdown therefore measures total time-to-load, not a single request.
 *
 * Exposed as a signal so any component can show a "slow connection" treatment.
 */
@Injectable({
  providedIn: 'root',
})
export class SlowConnectionService {
  /** A loading run is considered slow once it has been going this long. */
  private readonly thresholdMs = 3000;

  /**
   * Idle gap tolerated between requests before a loading run is treated as over.
   * Bridges the short pauses between sequential requests so their time counts
   * towards the same run instead of resetting the countdown.
   */
  private readonly idleGraceMs = 750;

  private activeRequests = 0;
  private slowTimer: ReturnType<typeof setTimeout> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly slow = signal(false);

  /** `true` while the app has been loading past the threshold. */
  readonly isSlow = this.slow.asReadonly();

  requestStarted(): void {
    this.activeRequests++;

    // Activity resumed — cancel any pending idle reset so sequential requests
    // count as one continuous run and no elapsed time is lost between them.
    this.clearIdleTimer();

    // Start the countdown once per run; don't restart it on every request.
    if (this.slowTimer === null && !this.slow()) {
      this.slowTimer = setTimeout(() => this.slow.set(true), this.thresholdMs);
    }
  }

  requestEnded(): void {
    this.activeRequests = Math.max(0, this.activeRequests - 1);

    if (this.activeRequests === 0) {
      // Wait a short grace period; if nothing else starts, the run is over.
      this.clearIdleTimer();
      this.idleTimer = setTimeout(() => this.endRun(), this.idleGraceMs);
    }
  }

  private endRun(): void {
    this.clearSlowTimer();
    this.clearIdleTimer();
    this.slow.set(false);
  }

  private clearSlowTimer(): void {
    if (this.slowTimer !== null) {
      clearTimeout(this.slowTimer);
      this.slowTimer = null;
    }
  }

  private clearIdleTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }
}
